/**
 * Small text helpers, site-wide.
 *
 * Exists because of `1 MOVES · 1 LINES` in the repertoire header — the kind of
 * thing that is invisible in a mock full of plausible numbers and obvious the
 * first time a real person builds their first line, which is exactly the
 * moment the count is one. The mono-uppercase house style makes it *louder*,
 * not quieter: `1 MOVES` reads as a template that was never finished.
 *
 * There is deliberately no `Intl.PluralRules` here. The site is English-only,
 * the irregulars in it are countable on one hand, and a caller that needs one
 * passes it explicitly.
 */

/**
 * `1 move`, `2 moves`, `0 moves`. Pass `many` for anything English does not
 * form with a bare `s`.
 *
 *     plural(1, 'move')            // '1 move'
 *     plural(3, 'line')            // '3 lines'
 *     plural(1, 'is', 'are')       // '1 is'
 */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * A frequency, for a knob. `420 Hz`, `8.6 kHz`, and `open` for a filter that is
 * not filtering.
 *
 * Whole hertz below a kilohertz and one decimal above: the second decimal of a
 * kilohertz is 10 Hz, which is under a tenth of a semitone up there and is
 * noise on a control the eye is reading while the hand turns it.
 *
 * `Infinity` is `open` rather than a large number because a one-pole at
 * coefficient zero is a wire, and a wire genuinely does not have a corner
 * frequency — see `cutoffFromPole` in `@/audio/onePole`.
 */
export function hzLabel(hz: number): string {
  if (!Number.isFinite(hz)) return 'open'
  return hz >= 1000 ? `${(hz / 1000).toFixed(1)} kHz` : `${Math.round(hz)} Hz`
}

/** The noun alone, already counted elsewhere. `moves` / `move`. */
export function pluralWord(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many
}
